// src/services/authService.ts - CORRIGIDO
import { supabase } from "../lib/supabase/client";
import type { Database } from "../lib/supabase/database.types";

interface UserSession {
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  expires_at: number;
}

interface AuthResponse {
  success: boolean;
  user?: any;
  error?: string;
  message?: string;
}

type AppUserRow = Database["public"]["Tables"]["app_users"]["Row"];

/**
 * Serviço de autenticação customizada usando tabela app_users
 */
export const authService = {
  /**
   * Registro de novo usuário com hash de senha
   */
  async register(
    email: string,
    password: string,
    userData: { name?: string; phone?: string }
  ): Promise<AuthResponse> {
    try {
      // Validações básicas
      if (!email || !password) {
        throw new Error("Email e senha são obrigatórios");
      }

      if (password.length < 6) {
        throw new Error("Senha deve ter pelo menos 6 caracteres");
      }

      // Verifica se usuário já existe
      const { data: existingUser, error: queryError } = await supabase
        .from("app_users")
        .select("id")
        .eq("email", email.toLowerCase())
        .single();

      if (queryError && queryError.code !== "PGRST116") {
        // PGRST116 = no rows
        throw queryError;
      }

      if (existingUser) {
        throw new Error("Usuário já cadastrado com este email");
      }

      // Cria hash da senha (em produção, use bcrypt no servidor)
      const passwordHash = await this.hashPassword(password);

      // Insere novo usuário - CORREÇÃO DA TIPAGEM AQUI
      const payload: Database["public"]["Tables"]["app_users"]["Insert"] = {
        email: email.toLowerCase(),
        password_hash: passwordHash,
        name: userData.name || null,
        phone: userData.phone || null,
        is_active: true,
      };

      const table = supabase.from("app_users" as any) as any;
      const { data, error } = await table
        .insert(payload as any)
        .select()
        .single();


      if (error) throw error;

      return {
        success: true,
        user: data,
        message: "Usuário criado com sucesso",
      };
    } catch (error) {
      console.error("Erro no registro:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      };
    }
  },

  /**
   * Login do usuário
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      // Busca usuário pelo email
      const { data: user, error } = await supabase
        .from("app_users")
        .select("id, email, password_hash, name, is_active")
        .eq("email", email.toLowerCase())
        .eq("is_active", true)
        .single();

      if (error || !user) {
        throw new Error("Credenciais inválidas");
      }

      const appUser = user as AppUserRow;

      // Verifica senha (em produção, use bcrypt.compare)
      const isValidPassword = await this.verifyPassword(
        password,
        appUser.password_hash
      );

      if (!isValidPassword) {
        throw new Error("Credenciais inválidas");
      }

      // Cria sessão local (substitui auth.session do Supabase)
      const session: UserSession = {
        user: {
          id: appUser.id,
          email: appUser.email,
          name: appUser.name,
        },
        expires_at: Date.now() + 24 * 60 * 60 * 1000, // 24 horas
      };

      this.setLocalSession(session);

      return {
        success: true,
        user: session.user,
        message: "Login realizado com sucesso",
      };
    } catch (error) {
      console.error("Erro no login:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      };
    }
  },

  /**
   * Logout do usuário
   */
  async logout(): Promise<AuthResponse> {
    try {
      this.clearLocalSession();
      return { success: true, message: "Logout realizado com sucesso" };
    } catch (error) {
      console.error("Erro no logout:", error);
      return { success: false, error: "Erro ao fazer logout" };
    }
  },

  /**
   * Verifica se usuário está autenticado
   */
  isAuthenticated(): boolean {
    const session = this.getLocalSession();
    return !!(session && session.expires_at > Date.now());
  },

  /**
   * Obtém usuário atual
   */
  getCurrentUser() {
    const session = this.getLocalSession();
    return session?.user || null;
  },

  // --- Métodos Auxiliares Privados ---

  /**
   * Hash de senha simplificado (SUBSTITUA por bcrypt em produção!)
   */
  async hashPassword(password: string): Promise<string> {
    // ⚠️ EM PRODUÇÃO: Use bcrypt ou similar no backend
    const encoder = new TextEncoder();
    const data = encoder.encode(password + "your-secret-salt");
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  },

  /**
   * Verificação de senha
   */
  async verifyPassword(password: string, hash: string): Promise<boolean> {
    const newHash = await this.hashPassword(password);
    return newHash === hash;
  },

  /**
   * Gerenciamento de sessão local
   */
  setLocalSession(session: UserSession): void {
    localStorage.setItem("legal_app_session", JSON.stringify(session));
  },

  getLocalSession(): UserSession | null {
    try {
      const session = localStorage.getItem("legal_app_session");
      return session ? JSON.parse(session) : null;
    } catch {
      return null;
    }
  },

  clearLocalSession(): void {
    localStorage.removeItem("legal_app_session");
  },
};